import { useEffect, useState, type DragEvent } from "react";
import {
  Box,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../dialog";
import { AppAutocomplete, AppTextField } from "../form";
import VisualFormatControls from "../VisualFormatControls";
import { dashboardService } from "../../services/dashboardService";
import type { DashboardQuery } from "../../types/query";
import type {
  DashboardVisualPlacement,
  QueryColumnSchema,
  VisualWellField,
  VizTypeSpec,
} from "../../types/dashboard";
import {
  FIELD_DRAG_MIME,
  decodeDragField,
  encodeDragField,
} from "../../utils/visualDesignerDrag";

type Props = {
  open: boolean;
  visual: DashboardVisualPlacement | null;
  columns?: QueryColumnSchema[];
  schemasByQueryId?: Record<string, { columns: QueryColumnSchema[]; note?: string }>;
  queries: DashboardQuery[];
  onClose: () => void;
  onApply: (visual: DashboardVisualPlacement) => void;
  initialTab?: number;
};

export default function VisualEditDialog({
  open,
  visual,
  columns = [],
  schemasByQueryId = {},
  queries,
  onClose,
  onApply,
  initialTab = 0,
}: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState<DashboardVisualPlacement | null>(null);
  const [catalog, setCatalog] = useState<VizTypeSpec[]>([]);
  const [dropWellId, setDropWellId] = useState<string>();

  useEffect(() => {
    if (!open || !visual) {
      setDraft(null);
      return;
    }
    setDraft(structuredClone(visual));
    setTab(initialTab);
    setDropWellId(undefined);
  }, [open, visual, initialTab]);

  useEffect(() => {
    if (!open) return;
    dashboardService.vizCatalog().then((response) => setCatalog(response.types || [])).catch(console.error);
  }, [open]);

  const spec = catalog.find((item) => item.vizType === draft?.vizType);
  const activeQueryId = draft?.queryId || "";
  const activeColumns =
    (activeQueryId && schemasByQueryId[activeQueryId]?.columns) || columns;
  const activeNote = activeQueryId ? schemasByQueryId[activeQueryId]?.note : undefined;

  const setWell = (
    wellId: string,
    field: string,
    multiple: boolean,
    asMeasure?: boolean,
    sourceQueryId?: string
  ) => {
    if (!draft) return;
    const nextQueryId = sourceQueryId || draft.queryId;
    const switching = !!sourceQueryId && !!draft.queryId && sourceQueryId !== draft.queryId;
    const baseWells = switching ? {} : draft.wells;
    const well = spec?.wells.find((item) => item.id === wellId);
    const current = baseWells?.[wellId] || [];
    const nextField: VisualWellField = {
      field,
      aggregate: well?.accepts === "measure" || asMeasure ? "sum" : undefined,
    };
    setDraft({
      ...draft,
      queryId: nextQueryId,
      wells: {
        ...baseWells,
        [wellId]: multiple ? [...current, nextField] : [nextField],
      },
    });
  };

  const dropFieldOnWell = (wellId: string, multiple: boolean, event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDropWellId(undefined);
    const payload = decodeDragField(event.dataTransfer.getData(FIELD_DRAG_MIME));
    if (!payload?.name) return;
    const asMeasure = payload.role === "measure" || payload.type === "number";
    setWell(wellId, payload.name, multiple, asMeasure, payload.queryId);
  };

  const handleApply = () => {
    if (!draft) return;
    onApply(structuredClone(draft));
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <AppDialogTitle>
        {t("reportDesigner.editVisualDialogTitle", {
          type: draft?.vizType || visual?.vizType || "",
        })}
      </AppDialogTitle>
      <AppDialogContent sx={{ gap: 1.5, minHeight: 360 }}>
        {draft && (
          <>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
              <Tab label={t("reportDesigner.build")} />
              <Tab label={t("reportDesigner.format")} />
            </Tabs>

            {tab === 0 && (
              <Stack spacing={1.5}>
                <AppAutocomplete
                  size="small"
                  fullWidth
                  label={t("reportDesigner.query")}
                  options={queries}
                  value={queries.find((q) => q.id === draft.queryId) ?? null}
                  getOptionLabel={(query) => query.name || query.id || ""}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_, next) => {
                    const nextQueryId = next?.id || undefined;
                    const switching = !!draft.queryId && nextQueryId !== draft.queryId;
                    setDraft({
                      ...draft,
                      queryId: nextQueryId,
                      wells: switching ? {} : draft.wells,
                    });
                  }}
                  helperText={t("reportDesigner.querySwitchHint")}
                />

                <AppTextField
                  size="small"
                  fullWidth
                  label={t("reportDesigner.title")}
                  value={draft.title || ""}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                />

                <Typography variant="caption" color="text.secondary">
                  {t("reportDesigner.wellDropHint")}
                </Typography>
                {activeNote && (
                  <Typography variant="caption" color="warning.main">
                    {activeNote}
                  </Typography>
                )}

                {spec ? (
                  spec.wells.map((well) => (
                    <Box
                      key={well.id}
                      onDragOver={(event) => {
                        if ([...event.dataTransfer.types].includes(FIELD_DRAG_MIME)) {
                          event.preventDefault();
                          setDropWellId(well.id);
                        }
                      }}
                      onDragLeave={() =>
                        setDropWellId((current) => (current === well.id ? undefined : current))
                      }
                      onDrop={(event) => dropFieldOnWell(well.id, well.multiple, event)}
                      sx={{
                        p: 1.25,
                        borderRadius: 1,
                        border: "1px dashed",
                        borderColor: dropWellId === well.id ? "primary.main" : "divider",
                        bgcolor: dropWellId === well.id ? "action.selected" : "action.hover",
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {well.label}
                        {well.required ? " *" : ""}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.75, minHeight: 28, gap: 0.5 }}
                      >
                        {(draft.wells?.[well.id] || []).map((item, index) => (
                          <Chip
                            key={`${item.field}-${index}`}
                            size="small"
                            label={item.aggregate ? `${item.aggregate}(${item.field})` : item.field}
                            onDelete={() => {
                              const next = [...(draft.wells?.[well.id] || [])];
                              next.splice(index, 1);
                              setDraft({
                                ...draft,
                                wells: { ...draft.wells, [well.id]: next },
                              });
                            }}
                          />
                        ))}
                        {!(draft.wells?.[well.id]?.length) && (
                          <Typography variant="caption" color="text.secondary">
                            {t("reportDesigner.wellEmpty")}
                          </Typography>
                        )}
                      </Stack>
                      {activeColumns.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 1 }}>
                          {activeColumns.map((column) => (
                            <Chip
                              key={`${well.id}-${column.name}`}
                              size="small"
                              variant="outlined"
                              label={column.name}
                              draggable
                              onDragStart={(event) => {
                                event.dataTransfer.setData(
                                  FIELD_DRAG_MIME,
                                  encodeDragField(column, activeQueryId || undefined)
                                );
                                event.dataTransfer.effectAllowed = "copy";
                              }}
                              onClick={() =>
                                setWell(
                                  well.id,
                                  column.name,
                                  well.multiple,
                                  column.role === "measure" || column.type === "number",
                                  activeQueryId || undefined
                                )
                              }
                              sx={{ cursor: "pointer" }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="caption" color="warning.main">
                    {t("reportDesigner.catalogLoading")}
                  </Typography>
                )}
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={1.5}>
                <VisualFormatControls
                  value={draft.format}
                  onChange={(format) => setDraft({ ...draft, format })}
                />
                <Divider />
                <AppTextField
                  size="small"
                  fullWidth
                  type="number"
                  label={t("reportDesigner.limit")}
                  value={draft.limit ?? ""}
                  onChange={(event) => {
                    const raw = event.target.value;
                    setDraft({
                      ...draft,
                      limit: raw === "" ? undefined : Number(raw),
                    });
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </AppDialogContent>
      <AppDialogActions>
        <AppDialogCancelButton type="button" onClick={onClose}>
          {t("common.cancel")}
        </AppDialogCancelButton>
        <AppDialogPrimaryButton type="button" onClick={handleApply} disabled={!draft}>
          {t("common.apply")}
        </AppDialogPrimaryButton>
      </AppDialogActions>
    </AppDialog>
  );
}
